import { useAuthInvitationConfirm } from "@boundless/bl-api-client/auth/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { FormCheckbox } from "@/components/form-elements/FormCheckbox"
import { FormConsentText } from "@/components/form-elements/FormConsentText"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormInputPhone } from "@/components/form-elements/FormInputPhone"
import { Button, LoadingButton } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import { invitationFormSchema } from "@/lib/schemas"

import type { ValidToken } from "@boundless/bl-api-client/auth/models"
import type { z } from "zod"

interface Props {
  invitation: ValidToken
  onSuccess: () => void
}

export function InvitationForm({ invitation, onSuccess }: Props) {
  const form = useForm<z.infer<typeof invitationFormSchema>>({
    resolver: zodResolver(invitationFormSchema),
    defaultValues: {
      password: "",
      passwordConfirm: "",
      hasAcceptedTerms: false,
    },
  })

  const { mutate: confirm, isPending } = useAuthInvitationConfirm({
    mutation: {
      onSuccess: () => {
        onSuccess()
      },
      onError: (error) => {
        if (error.status === 400) {
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          )
        } else {
          toast.error(error.message)
        }
      },
    },
  })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) =>
          confirm({ uuid: invitation.uuid, data }),
        )}
      >
        <div className="flex flex-col space-y-4">
          <Input placeholder={invitation.firstName} disabled />
          <Input placeholder={invitation.lastName} disabled />
          <Input placeholder={invitation.email} disabled />

          <FormInputPhone name="phone" placeholder="Phone" />
          <FormInput
            name="password"
            placeholder="Password"
            type="password"
            autoComplete="new-password"
          />
          <FormInput
            name="passwordConfirm"
            placeholder="Confirm password"
            type="password"
            autoComplete="new-password"
          />

          <div className="px-8 py-2">
            <FormCheckbox
              name="hasAcceptedTerms"
              className="size-6 self-center"
              label={<FormConsentText action="checking this box" />}
            />
          </div>

          {isPending ? (
            <LoadingButton />
          ) : (
            <Button type="submit">Create Account</Button>
          )}
        </div>
      </form>
    </Form>
  )
}
